<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import PanelComponent from './components/PanelComponent.vue'
import MatrixComponent from './components/MatrixComponent.vue'
import { fetchCategories, fetchRadiators } from './lib/api'
import { resolveDefaultView } from './lib/calculator-defaults'
import { getStrings, resolveLang, resolveLocale } from './lib/i18n'
import { trackEvent } from './lib/tracking'

const props = defineProps({
  market: {
    type: String,
    default: '',
  },
  category: {
    type: String,
    default: '',
  },
  productId: {
    type: String,
    default: '',
  },
  lang: {
    type: String,
    default: '',
  },
  showEudop: {
    type: [Boolean, String],
    default: false,
  },
  hideColumns: {
    type: String,
    default: '',
  },
  view: {
    type: String,
    default: '',
  },
})

const isWidgetMode = () => typeof window !== 'undefined' && window.__WIDGET_MODE__ === true

const getProductIdFromUrl = () => {
  if (isWidgetMode()) return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('productId') || params.get('product-id') || ''
}

const getMarketFromUrl = () => {
  if (isWidgetMode()) return 'UKMyson'
  const params = new URLSearchParams(window.location.search)
  return params.get('market') || 'UKMyson'
}

const getCategoryFromUrl = () => {
  if (isWidgetMode()) return 'panels'
  const params = new URLSearchParams(window.location.search)
  return params.get('category') || 'panels'
}

const setUrlParam = (key, value) => {
  if (isWidgetMode()) return
  const url = new URL(window.location.href)
  if (value) {
    url.searchParams.set(key, value)
  } else {
    url.searchParams.delete(key)
  }
  window.history.replaceState({}, '', url)
}

const getViewFromUrl = () => {
  if (isWidgetMode()) return ''
  return new URLSearchParams(window.location.search).get('view') || ''
}

const market = ref(props.market || getMarketFromUrl())
const language = computed(() => props.lang || resolveLang(market.value))
const exportLang = computed(() => resolveLocale(props.lang || language.value, market.value))
const showEudop = computed(
  () => props.showEudop === true || props.showEudop === 'true' || props.showEudop === ''
)
const strings = computed(() => getStrings(language.value))
// An explicit view (attribute or ?view=) wins over the market's default
const resolvedView = computed(
  () => props.view || getViewFromUrl() || resolveDefaultView(market.value)
)
const categories = ref([])
const resolvedProductId = computed(() => props.productId || getProductIdFromUrl())
const selectedCategory = ref(
  props.category || (resolvedProductId.value ? '' : getCategoryFromUrl())
)
const isLoadingCategories = ref(false)
const categoryError = ref('')
const radiators = ref([])
const panelOptions = ref([])
const selectedPanel = ref(resolvedProductId.value || '')
const isLoading = ref(false)
const loadError = ref('')
const productNotFound = ref(false)
const isProductLocked = computed(() => Boolean(resolvedProductId.value))
const isCategoryLocked = computed(() => Boolean(props.category) || isProductLocked.value)
const headerTitle = computed(() => {
  const category = selectedCategory.value
  const titles = strings.value.app?.titles || {}
  return titles[category] || strings.value.app?.title || strings.value.calc?.title || ''
})

const toOption = (item) => {
  if (item == null) return null
  if (typeof item === 'string' || typeof item === 'number') {
    return { label: String(item), value: String(item) }
  }

  if (typeof item === 'object') {
    const label =
      item.label ??
      item.name ??
      item.title ??
      item.code ??
      item.productId ??
      item.id ??
      JSON.stringify(item)
    const value = item.value ?? item.productId ?? item.id ?? item.code ?? label
    return { label: String(label), value: String(value) }
  }

  return null
}

const getCategoryLabel = (value) =>
  strings.value.app.categories?.[value] ? strings.value.app.categories[value] : value

const loadPanels = async () => {
  if (!selectedCategory.value) {
    panelOptions.value = []
    radiators.value = []
    if (!isProductLocked.value) {
      selectedPanel.value = ''
    }
    return
  }

  isLoading.value = true
  loadError.value = ''
  const requested = `${market.value}|${selectedCategory.value}`
  try {
    const data = await fetchRadiators(market.value, selectedCategory.value, exportLang.value)
    // A newer category/market change superseded this request
    if (requested !== `${market.value}|${selectedCategory.value}`) return
    const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
    radiators.value = list
    panelOptions.value = list.map(toOption).filter(Boolean).sort((a, b) => a.label.localeCompare(b.label))
    trackEvent('calculator_product_loaded', {
      market: market.value,
      category: selectedCategory.value,
      product_count: list.length,
    })
    // Start on the first radiator of the list instead of an empty selection
    if (!isProductLocked.value && !selectedPanel.value && panelOptions.value.length) {
      selectedPanel.value = panelOptions.value[0].value
    }
  } catch (error) {
    loadError.value = error?.message ?? strings.value.app.errorPanels
    radiators.value = []
    panelOptions.value = []
  } finally {
    if (requested === `${market.value}|${selectedCategory.value}`) isLoading.value = false
  }
}

const loadCategories = async () => {
  isLoadingCategories.value = true
  categoryError.value = ''
  try {
    const data = await fetchCategories(exportLang.value)
    const list = Array.isArray(data) ? data : Array.isArray(data?.categories) ? data.categories : []
    categories.value = list
      .map(toOption)
      .filter(Boolean)
      .map((option) => ({
        ...option,
        label: getCategoryLabel(option.value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    if (!selectedCategory.value && categories.value.length > 0) {
      selectedCategory.value = categories.value[0].value
    }
  } catch (error) {
    categoryError.value = error?.message ?? strings.value.app.errorCategories
    categories.value = []
  } finally {
    isLoadingCategories.value = false
  }
}

const categoryInitialized = ref(false)
watch(
  selectedCategory,
  () => {
    if (!categoryInitialized.value) {
      categoryInitialized.value = true
    } else if (selectedCategory.value) {
      trackEvent('calculator_category_change', {
        category: selectedCategory.value,
        market: market.value,
      })
    }
    selectedPanel.value = isProductLocked.value ? props.productId : ''
    setUrlParam('category', selectedCategory.value)
    loadPanels()
  },
  { immediate: true }
)

watch(selectedPanel, (val) => {
  if (!val) return
  trackEvent('calculator_product_change', {
    product_name: panelOptions.value.find((o) => o.value === val)?.label,
    category: selectedCategory.value,
    market: market.value,
  })
})

watch(
  () => props.market,
  (value) => {
    if (!value) return
    market.value = value
    selectedPanel.value = ''
    loadPanels()
  }
)

watch(
  () => props.category,
  (value) => {
    if (!value) return
    selectedCategory.value = value
    selectedPanel.value = ''
    loadPanels()
  }
)

watch(
  () => props.productId,
  (value) => {
    if (!value) return
    selectedPanel.value = value
    productNotFound.value = false
    if (selectedCategory.value) {
      loadPanels()
    }
  }
)

watch(
  () => resolvedProductId.value,
  (value) => {
    if (!value) return
    selectedPanel.value = value
    productNotFound.value = false
  }
)

const selectedRadiator = computed(() => {
  const id = selectedPanel.value
  if (!id) return null
  return radiators.value.find((item) => item?.productId === id || item?.id === id) || null
})

onMounted(() => {
  if (!isCategoryLocked.value && !isProductLocked.value) {
    loadCategories()
  }
})
</script>

<template>
  <div v-if="!productNotFound" class="app-root">
    <div class="panel-picker">
      <!-- <h1>{{ headerTitle }}</h1> -->
      <div v-if="!isCategoryLocked" class="select-wrap">
        <label class="visually-hidden" for="category-select">{{ strings.app.categoryLabel }}</label>
        <select
          id="category-select"
          v-model="selectedCategory"
          :disabled="isLoadingCategories || categories.length === 0"
        >
          <option value="" disabled>{{ strings.app.categoryPlaceholder }}</option>
          <option v-for="option in categories" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <p v-if="!isCategoryLocked && isLoadingCategories" class="status">
        {{ strings.app.loadingCategories }}
      </p>
      <p v-else-if="!isCategoryLocked && categoryError" class="status error">
        {{ categoryError }}
      </p>
      <div v-if="!isProductLocked" class="select-wrap">
        <label class="visually-hidden" for="panel-select">{{ strings.app.panelLabel }}</label>
        <select
          id="panel-select"
          v-model="selectedPanel"
          :disabled="isLoading || panelOptions.length === 0"
        >
          <option value="" disabled>{{ strings.app.panelPlaceholder }}</option>
          <option v-for="option in panelOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <p v-if="!isProductLocked && isLoading" class="status">{{ strings.app.loadingPanels }}</p>
      <p v-else-if="!isProductLocked && loadError" class="status error">{{ loadError }}</p>
    </div>

    <PanelComponent
      v-if="resolvedView !== 'matrix'"
      :product-id="selectedPanel"
      :product="selectedRadiator"
      :category="selectedCategory"
      :market="market"
      :lang="exportLang"
      :strings="strings"
      :show-eudop="showEudop"
      :hide-columns="props.hideColumns"
      @product-not-found="productNotFound = $event"
    />
    <MatrixComponent
      v-else
      :product-id="selectedPanel"
      :product="selectedRadiator"
      :category="selectedCategory"
      :market="market"
      :lang="exportLang"
      :strings="strings"
      :show-eudop="showEudop"
      :hide-columns="props.hideColumns"
    />
  </div>
  <div v-else class="app-root"></div>
</template>

<style src="./styles/base.css"></style>

<style scoped>
.panel-picker {
  display: grid;
  gap: 16px;
  justify-items: center;
  padding: 0 16px 8px;
  color: var(--semantic-text-black);
  max-width: 100%;
  box-sizing: border-box;
}

h1 {
  margin: 0;
  font-size: clamp(22px, 2.6vw, 28px);
  font-weight: 700;
  letter-spacing: 0.3px;
}

.select-wrap {
  position: relative;
  width: min(560px, 100%);
}

select {
  width: 100%;
  height: 40px;
  padding: 8px 44px 8px 16px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: #ffffff;
  color: var(--semantic-text-default);
  font-size: 14px;
  appearance: none;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 18px 18px;
}

select:disabled {
  color: #8aa0a3;
  background-color: var(--color-grey-200);
}

.status {
  margin: 0;
  font-size: 12px;
  color: #6b7b7f;
}

.error {
  color: #b93838;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>

<style scoped>
.logo {
  height: 6em;
  padding: 1.5em;
  will-change: filter;
  transition: filter 300ms;
}
.logo:hover {
  filter: drop-shadow(0 0 2em #646cffaa);
}
.logo.vue:hover {
  filter: drop-shadow(0 0 2em #42b883aa);
}
</style>
